import { useEffect, useMemo, useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import type { Advertiser } from "@/api/hooks/campaignForm"

/**
 * Advertiser autocomplete. Ports `backend/static/js/advertiser.js`:
 *
 *   - Type-to-filter over the in-memory cache.
 *   - Arrow Up / Arrow Down highlight; Enter commits; Escape closes.
 *   - Click-outside closes the dropdown.
 *   - Empty states: "No TikTok accounts connected" (empty cache) vs.
 *     "No matches found" (cache present but filtered to zero).
 *   - Loading / error placeholders in the input itself.
 *   - Disables input while loading or empty.
 */

interface Props {
  advertisers: Advertiser[] | undefined
  isLoading: boolean
  isError: boolean
  value: string | null
  onSelect: (id: string | null, label: string) => void
}

interface Option {
  id: string
  name: string
  label: string
  searchLabel: string
}

function formatLabel(adv: Advertiser): string {
  const name = (adv.name || "").trim()
  if (!name || name === adv.id) return adv.id
  return `${name} – ${adv.id}`
}

export function AdvertiserAutocomplete({
  advertisers,
  isLoading,
  isError,
  value,
  onSelect,
}: Props) {
  const options: Option[] = useMemo(() => {
    if (!advertisers) return []
    return advertisers
      .filter((adv) => adv.id)
      .map((adv) => {
        const label = formatLabel(adv)
        return {
          id: adv.id,
          name: adv.name ?? adv.id,
          label,
          searchLabel: `${label} ${adv.name ?? ""}`.toLowerCase(),
        }
      })
  }, [advertisers])

  const [text, setText] = useState("")
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Reset the visible text when `value` flips externally (e.g. parent
  // clears advertiser on error recovery). Adjusted during render rather
  // than in an effect so the cleared text lands in the same commit.
  const [prevValue, setPrevValue] = useState(value)
  if (value !== prevValue) {
    setPrevValue(value)
    if (value === null) setText("")
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase()
    if (!q) return options.slice(0, 60)
    return options.filter((o) => o.searchLabel.includes(q)).slice(0, 60)
  }, [options, text])

  const isEmpty = !isLoading && !isError && options.length === 0
  const disabled = isLoading || isError || isEmpty

  let placeholder = "Search accounts..."
  if (isLoading) placeholder = "Loading accounts..."
  else if (isError) placeholder = "Failed to load accounts"
  else if (isEmpty) placeholder = "No TikTok accounts connected"

  const commit = (opt: Option) => {
    setText(opt.label)
    setOpen(false)
    setActiveIndex(-1)
    onSelect(opt.id, opt.label)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) return
    if (!open) setOpen(true)
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => (filtered.length === 0 ? -1 : (i + 1) % filtered.length))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) =>
        filtered.length === 0 ? -1 : (i - 1 + filtered.length) % filtered.length,
      )
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && filtered[activeIndex]) {
        e.preventDefault()
        commit(filtered[activeIndex])
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Shadow input for native <form required> validation. The visible
          <Input> carries the search query (not the committed id), so the
          real validation target is a hidden mirror of `value`. */}
      <input
        tabIndex={-1}
        aria-hidden="true"
        required
        value={value ?? ""}
        onChange={() => {}}
        disabled={disabled}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
      <Input
        type="text"
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        autoComplete="off"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-owns="advertiser-results"
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
          setActiveIndex(-1)
          // Clear any committed selection while the user edits.
          if (value !== null) onSelect(null, "")
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <div
          id="advertiser-results"
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-brand-border bg-brand-bg shadow-lg"
        >
          {filtered.length > 0 ? (
            filtered.map((opt, index) => (
              <div
                role="option"
                aria-selected={index === activeIndex}
                key={opt.id}
                className={`flex cursor-pointer flex-col px-3 py-2 text-sm hover:bg-brand-border/40 ${
                  index === activeIndex ? "bg-brand-border/60" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  commit(opt)
                }}
              >
                <span className="font-medium text-brand-fg">{opt.name}</span>
                <span className="text-xs text-white">{opt.id}</span>
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-white">
              {text.trim().length > 0
                ? "No matches found"
                : "No TikTok accounts connected"}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
